import { observer } from "mobx-react";
import React from "react";
import { useInjection } from "inversify-react";
import { AppController } from "@dropDesk/presentation/app/app.controller";
import DropDeskIcon from "@dropDesk/storage/icons";
import { hexToRGBA } from "@dropDesk/utils/helpers/colors";
import {
    ComparativeMetric
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/comparative_metric.entity";
import {
    DirectionIcon,
    formatVariationPercent,
    getPillTone,
} from "@dropDesk/presentation/components/metrics_cards/variation_helpers";
import {
    Card,
    CardHeader,
    CardTitle,
    CurrentValue,
    IconCircle,
    PreviousValue,
    SecondaryValue,
    SkeletonBone,
    ValueGroup,
    ValueLabel,
    ValueRow,
    ValuesBlock,
    ValuesDivider,
    VariationPercent,
    VariationPill,
} from "./styled";

type SecondaryMetricProps = {
    label: string;
    metric: ComparativeMetric;
    formatValue: (value: number) => string;
};

const MetricCard = observer(({
    title,
    metric,
    formatValue,
    icon,
    iconColor,
    iconBackground,
    secondaryMetric,
    withComparative = true,
    loading = false,
}: {
    title: string;
    metric?: ComparativeMetric;
    formatValue?: (value: number) => string;
    icon: string;
    iconColor: string;
    iconBackground: string;
    secondaryMetric?: SecondaryMetricProps;
    withComparative?: boolean;
    loading?: boolean;
}) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const skeletonBackground = hexToRGBA(colors.border, 0.55);
    const skeletonHighlight = hexToRGBA(colors.hint, 0.35);

    if (loading || !metric || !formatValue) {
        return (
            <Card background={colors.onBackground} border={colors.border}>
                <CardHeader>
                    <IconCircle background={iconBackground}>
                        <DropDeskIcon icon={icon} size={16} color={iconColor} />
                    </IconCircle>
                    <CardTitle color={colors.text}>{title}</CardTitle>
                </CardHeader>

                <ValuesBlock>
                    <ValueGroup>
                        {withComparative && (
                            <ValueLabel color={colors.accent}>Atual</ValueLabel>
                        )}
                        <ValueRow>
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="72px"
                                height="24px"
                            />
                            {!!secondaryMetric && (
                                <SkeletonBone
                                    background={skeletonBackground}
                                    highlight={skeletonHighlight}
                                    width="110px"
                                    height="12px"
                                />
                            )}
                        </ValueRow>
                    </ValueGroup>
                    {withComparative && (
                        <>
                            <ValuesDivider border={hexToRGBA(colors.border, 0.7)} />
                            <ValueGroup>
                                <ValueLabel color={colors.hint}>Anterior</ValueLabel>
                                <ValueRow>
                                    <SkeletonBone
                                        background={skeletonBackground}
                                        highlight={skeletonHighlight}
                                        width="56px"
                                        height="16px"
                                    />
                                    {!!secondaryMetric && (
                                        <SkeletonBone
                                            background={skeletonBackground}
                                            highlight={skeletonHighlight}
                                            width="110px"
                                            height="12px"
                                        />
                                    )}
                                </ValueRow>
                            </ValueGroup>
                        </>
                    )}
                </ValuesBlock>

                {withComparative && (
                    <SkeletonBone
                        background={skeletonBackground}
                        highlight={skeletonHighlight}
                        width="100%"
                        height="34px"
                        borderRadius="20px"
                    />
                )}
            </Card>
        );
    }

    const pillTone = getPillTone(metric.direction, metric.isImprovement, colors.hint);
    const variationLabel = formatVariationPercent(metric.variationPercent);

    return (
        <Card background={colors.onBackground} border={colors.border}>
            <CardHeader>
                <IconCircle background={iconBackground}>
                    <DropDeskIcon icon={icon} size={16} color={iconColor} />
                </IconCircle>
                <CardTitle color={colors.text}>{title}</CardTitle>
            </CardHeader>

            <ValuesBlock>
                <ValueGroup>
                    {withComparative && (
                        <ValueLabel color={colors.accent}>Atual</ValueLabel>
                    )}
                    <ValueRow>
                        <CurrentValue color={colors.text}>{formatValue(metric.current)}</CurrentValue>
                        {!!secondaryMetric && (
                            <SecondaryValue color={colors.hint}>
                                {secondaryMetric.label}: {secondaryMetric.formatValue(secondaryMetric.metric.current)}
                            </SecondaryValue>
                        )}
                    </ValueRow>
                </ValueGroup>
                {withComparative && (
                    <>
                        <ValuesDivider border={hexToRGBA(colors.border, 0.7)} />
                        <ValueGroup>
                            <ValueLabel color={colors.hint}>Anterior</ValueLabel>
                            <ValueRow>
                                <PreviousValue color={colors.hintInverse}>{formatValue(metric.previous)}</PreviousValue>
                                {!!secondaryMetric && (
                                    <SecondaryValue color={colors.hint}>
                                        {secondaryMetric.label}: {secondaryMetric.formatValue(secondaryMetric.metric.previous)}
                                    </SecondaryValue>
                                )}
                            </ValueRow>
                        </ValueGroup>
                    </>
                )}
            </ValuesBlock>

            {withComparative && (
                <VariationPill color={pillTone.color} background={pillTone.background}>
                    <DirectionIcon direction={metric.direction} color={pillTone.color} />
                    <VariationPercent>{variationLabel}</VariationPercent>
                    <span>Variação</span>
                </VariationPill>
            )}
        </Card>
    );
});

export default MetricCard;
